'use strict';

import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import {
    Box3, CircleGeometry,
    DoubleSide,
    Group, LinearSRGBColorSpace,
    Mesh, MeshBasicMaterial,
    TextureLoader,
    Vector3,
} from 'three';
import { GSAPAnimationMixer, projectMode, settingsData, threeJsVariables } from '../../settings';
import { getCurrentLang } from '../components/langSwitcher/langSwitcher';
import gsap from 'gsap';
import { rotateToCamera } from '../controls/orbitControls';
import { ViewMode } from '../../common';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader'
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js'
import {isCylinderDisplayModeEqual, RTL_LANGUAGES} from '../../functions';

export class CylinderBase {
    static #group = new Group();
    static target = null;
    static #active = config.features.cylinderBase.active;
    static #ready = false;
    static #correctionY = config.features.cylinderBase.correctionPosY || 0;
    static #seaAngle = config.features.cylinderBase.sea?.angel || 0;
    static #box = new Box3();
    static center = new Vector3();
    static #size = new Vector3();
    static #arrows = [];
    static #grid = null;
    static #rotateToCamera = [];
    static #rotation = new Vector3();
    static #divideSize = config.features.cylinderBase.arrows?.divideSize;
    static #NSDivider = config.features.cylinderBase.arrows?.NSDivideSize;
    static #EWDivider = config.features.cylinderBase.arrows?.EWDivideSize;
    #arrowScale = config.features.cylinderBase.arrows.scale || 1;
    #textDistance = config.features.cylinderBase.texts.distance || 15;
    #textSize = config.features.cylinderBase.texts.size || 6;
    #colorMode = config.resources.background.cylinderBg === 'sky' ? 'dark': 'light';
    #seaArrow = config.features.cylinderBase.sea || false;
    #colors = config.features.cylinderBase.colors || {};

    constructor () {
        if (!CylinderBase.#active || projectMode.LiteMode.isActive() && (projectMode.LiteMode.modeIs('picture') || projectMode.LiteMode.modeIs('empty')) || config.features.privateHouseMode || isCylinderDisplayModeEqual('show all')) {
            CylinderBase.#active = false;
            return;
        }

        const fbxLoader = new FBXLoader();
        const textureLoader = new TextureLoader();
        const fontLoader = new FontLoader();
        const PATH = 'assets/model/cylinder';

        let colors;

        switch (this.#colorMode) {
            case 'dark':
                colors = {
                    grid: this.#colors.grid ||'#7f7f7f',
                    arrows: this.#colors.arrows || '#2a2b2e',
                    northArrow: this.#colors.northArrow || '#ff0000',
                    text: this.#colors.text || '#000000',
                    sea: this.#colors.sea || '#0e52e6'
                };
                break;
            case 'light':
                colors = {
                    grid: this.#colors.grid || '#ffffff',
                    arrows: this.#colors.arrows || '#ffffff',
                    northArrow: this.#colors.northArrow || '#ff0000',
                    text: this.#colors.text || '#ffffff',
                    sea: this.#colors.sea || '#0e52e6'
                };
                break;
        }

        const grid = fbxLoader.loadAsync(PATH + '/Grid.fbx');
        const arrow = fbxLoader.loadAsync(PATH + '/Arrow.fbx');
        const texture = textureLoader.loadAsync(PATH + '/alpha_grid.jpg');
        const font = fontLoader.loadAsync('fonts/assistant/Assistant_Regular.json');
        const arrowBackground = this.#createArrowBackground();

        Promise.all([grid,arrow,texture,font]).then( async ( [ grid,arrow,texture,font ] ) => {

            const currentLang = getCurrentLang();
            let rusFont, arFont;

            if (currentLang === 'ar')
                this.#textSize *= 1.5;

            if (config.languages.includes('ru')){
               rusFont = await fontLoader.loadAsync('fonts/openSans/Open_Sans_Regular.json')
            }

            if (config.languages.includes('ar')) {
                arFont = await fontLoader.loadAsync('fonts/Amiri_Regular.json')
            }

            // Grid
            CylinderBase.#grid = grid.children[0];
            CylinderBase.#grid.layers.set(1);
            CylinderBase.#grid.material = new MeshBasicMaterial({
                color: colors.grid,
                transparent: true,
                opacity: 0,
                alphaMap: texture
            });
            CylinderBase.#group.add( CylinderBase.#grid);
            CylinderBase.#grid.visible = false;

            // Arrows
            const arrowMesh = arrow.children[0];
            arrowMesh.scale.set(this.#arrowScale,this.#arrowScale,this.#arrowScale);
            arrowMesh.visible = false;
            arrowMesh.position.y = 0.1;

            const arrows = {
                North: [-Math.PI / 2, 0, -Math.PI / 2],
                South: [-Math.PI / 2, 0, Math.PI / 2],
                West: [-Math.PI / 2, 0, 0],
                East: [-Math.PI / 2, 0, -Math.PI]
            };

            const textRotations = {
                North: Math.PI,
                South: 0,
                West: Math.PI / 2,
                East: -Math.PI / 2
            };

            if (this.#seaArrow) {
                arrows.Sea = [-Math.PI / 2, 0, 0];
                textRotations.Sea = 0;
            }


            Object.entries(arrows).forEach(([direction,rotation]) => {
                const directionArrow = arrowMesh.clone();
                directionArrow.layers.set(1);
                directionArrow.material = new MeshBasicMaterial();

                switch (direction) {
                    case 'North':
                        directionArrow.material.color.setStyle(colors.northArrow,LinearSRGBColorSpace);
                        break;
                    case 'Sea':
                        directionArrow.material.color.setStyle(colors.sea,LinearSRGBColorSpace);
                        break;
                    default:
                        directionArrow.material.color.setStyle(colors.arrows,LinearSRGBColorSpace);
                        break;
                }

                directionArrow.name = direction;
                directionArrow.rotation.set(rotation[0],rotation[1],rotation[2]);

                CylinderBase.#arrows.push(directionArrow);

                let dataFromDictionary = config.dictionary[direction];
                const exceptionName = direction + 'Grid';

                if (config.dictionary.hasOwnProperty(exceptionName)){
                    dataFromDictionary = config.dictionary[exceptionName];
                }

                config.languages.forEach(lang => {

                    let chosenFont = font;

                    if (lang === 'ru')
                        chosenFont = rusFont;
                    if (lang === 'ar')
                        chosenFont = arFont;

                    const fontParams = {
                        font: chosenFont,
                        size: this.#textSize,
                        depth: 0.2,
                        curveSegments: 6,
                        bevelEnabled: true,
                        bevelThickness: 0.03,
                        bevelSize: 0.02
                    };

                    let textFromDictionary = dataFromDictionary[lang];

                    if (RTL_LANGUAGES.includes(lang)){
                        textFromDictionary = dataFromDictionary[lang].split('').reverse().join('');
                    }

                    const geometry = new TextGeometry(textFromDictionary,fontParams);
                    geometry.center();
                    const material = new MeshBasicMaterial({
                        side: DoubleSide
                    });

                    switch (direction) {
                        case 'North':
                            material.color.setStyle(colors.northArrow,LinearSRGBColorSpace);
                            break;
                        case 'Sea':
                            material.color.setStyle(colors.sea,LinearSRGBColorSpace);
                            break;
                        default:
                            material.color.setStyle(colors.arrows,LinearSRGBColorSpace);
                            break;
                    }

                    const text = new Mesh(geometry,material);
                    text.layers.set(1);
                    text.name = direction + `_${lang}`;
                    text.rotation.set(Math.PI / 2, Math.PI, 0);
                    text.position.set(0, this.#textDistance, 0);
                    text.visible = false;
                    text.userData.cameraAngle = textRotations[direction];

                    if (lang === currentLang){
                        CylinderBase.#rotateToCamera.push(text);
                    }

                    directionArrow.add(text);
                });

                const background = arrowBackground.clone();
                background.layers.set(1);
                background.name = directionArrow.name;
                directionArrow.add(background);

                background.userData.type = 'arrow';
                directionArrow.userData.type = 'arrow';

                settingsData.additionalIntersectionObjects.push(background);
                // settingsData.additionalIntersectionObjects.push(directionArrow);

                CylinderBase.#group.add(directionArrow);
            });

            CylinderBase.#ready = true;

        });

        CylinderBase.#group.name = 'Cylinder Grids';
        CylinderBase.#rotation.y = config.threejs.compass.correction + Math.PI / 2;
        CylinderBase.#group.rotation.setFromVector3(CylinderBase.#rotation);

        threeJsVariables.scene.add(CylinderBase.#group);
        threeJsVariables.renderList.reorder();

    }

    #createArrowBackground() {
        const backgroundMaterial = new MeshBasicMaterial({
            transparent: true,
            opacity: 0,
            depthTest: false,
            depthWrite: false
        });
        const backgroundGeometry = new CircleGeometry(25);
        const backgroundMesh = new Mesh(backgroundGeometry, backgroundMaterial);
        backgroundMesh.scale.set(0.75,1,1);
        backgroundMesh.position.set(0, 10, -0.5);
        return backgroundMesh;
    }

    static activateVantageMode(vec3) {
        CylinderBase.#grid.visible = false;
        const arrowVector = new Vector3();

        CylinderBase.#arrows.forEach(arrow => {
            // arrow.traverse(el => {
            //     el.material.transparent = true;
            //     el.material.depthTest = false;
            // });

            arrow.scale.addScalar(0.25);
            arrowVector.copy(arrow.position).setLength(arrow.position.length() * 4);
            arrowVector.y = arrow.position.y;
            gsap.to(arrow.position,{
                x: arrowVector.x,
                y: arrowVector.y,
                z: arrowVector.z,
                duration: 1
            });
        });

        CylinderBase.#group.position.setY(vec3.y - 50);
    }

    static deactivateVantageMode() {
        CylinderBase.#arrows.forEach(arrow => {
            arrow.scale.subScalar(0.25);
        });
    }

    static show(building) {
        if (!CylinderBase.#active) return;

        if (!CylinderBase.#ready) {
            const interval = setInterval(() => {
                if (CylinderBase.#ready){
                    clearInterval(interval);
                    CylinderBase.show(building);
                }
            },100);

            return;
        }

        let buildingOnScene;

        if (projectMode.LiteMode.isActive() && projectMode.LiteMode.modeIs('schematic')){
            buildingOnScene = building.selectionsGroup;
        } else {
            buildingOnScene = threeJsVariables.scene.getObjectByName(building.name);
        }

        CylinderBase.#box.setFromObject(buildingOnScene).getSize(CylinderBase.#size);
        if (config.features.cylinderBase.geometricCenter) {
            CylinderBase.#box.getCenter(CylinderBase.center);
        } else {
            buildingOnScene.getWorldPosition(CylinderBase.center);
        }

        CylinderBase.#group.position.set(CylinderBase.center.x,buildingOnScene.position.y + CylinderBase.#correctionY, CylinderBase.center.z);

        const NSAxisSizeDivider = CylinderBase.#NSDivider || CylinderBase.#divideSize || 2;
        const EWAxisSizeDivider = CylinderBase.#EWDivider || CylinderBase.#divideSize || 2;
        const formulae = Math.sqrt(CylinderBase.#size.x * CylinderBase.#size.x + CylinderBase.#size.z * CylinderBase.#size.z);
        const NSAxisSize = formulae / NSAxisSizeDivider;
        const EWAxisSize = formulae / EWAxisSizeDivider;
        const seaSize = formulae / CylinderBase.#divideSize || 2;

        CylinderBase.#arrows.forEach(arrow => {
           let positionX, positionZ;

           switch (arrow.name) {
               case 'North':
                   positionX = 1;
                   positionZ = 0;
                   arrow.position.set(positionX * NSAxisSize,arrow.position.y,positionZ * NSAxisSize);
                   break;
               case 'South':
                   positionX = -1;
                   positionZ = 0;
                   arrow.position.set(positionX * NSAxisSize,arrow.position.y,positionZ * NSAxisSize);
                   break;
               case 'East':
                   positionX = 0;
                   positionZ = 1;
                   arrow.position.set(positionX * EWAxisSize,arrow.position.y,positionZ * EWAxisSize);
                   break;
               case 'West':
                   positionX = 0;
                   positionZ = -1;
                   arrow.position.set(positionX * EWAxisSize,arrow.position.y,positionZ * EWAxisSize);
                   break;
               case 'Sea':
                   positionX = 2;
                   positionZ = 0;
                   const seaVector = new Vector3(positionX * seaSize, arrow.position.y,positionZ * seaSize);
                   seaVector.applyAxisAngle(new Vector3(0,1,0).normalize(),CylinderBase.#seaAngle);
                   const angle = seaVector.angleTo(new Vector3(0,arrow.position.y,0));
                   arrow.userData.angle = CylinderBase.#seaAngle;
                   arrow.position.copy(seaVector);
                   arrow.rotation.z = CylinderBase.#seaAngle - angle;
                   arrow.children.forEach(text => {
                       text.userData.cameraAngle = Math.PI - CylinderBase.#seaAngle;
                   });
                   break;
           }

        });

        CylinderBase.#showAnimation();
    }

    static hide() {
        if (!CylinderBase.#active || !CylinderBase.#ready) return;
        //Arrows
        CylinderBase.#arrows.forEach(arrow => {
            arrow.traverse(el => el.visible = false);
        });

        //Grid
        CylinderBase.#grid.material.opacity = 0;
        CylinderBase.#grid.visible = false;
    }

    static visibility(state) {
        CylinderBase.#group.visible = state;
    }

    static #showAnimation() {
        const currentLang = getCurrentLang();
        let needsPlay = false;

        let gridMixer = GSAPAnimationMixer.getMixer('cylinderEnter');

        if (!gridMixer) {
            gridMixer = GSAPAnimationMixer.getMixer('switchingBuilding');
        }

        if (!gridMixer) {
            gridMixer = GSAPAnimationMixer.registerNewMixer('cylinderBase');
            needsPlay = true;
        }

        // gridMixer.addCallback('onStart',() => {
        //     CylinderBase.#grid.material.opacity = 0;
        //     CylinderBase.#grid.visible = true;
        // });

        // Grid
        const gridAnimation = gsap.to(CylinderBase.#grid.material,{
            opacity: 1,
            duration: GSAPAnimationMixer.duration,
            ease: GSAPAnimationMixer.ease,
            onStart: () => CylinderBase.#grid.visible = true
        });

        gridMixer.addAnimation(gridAnimation);

        //Arrows
        CylinderBase.#arrows.forEach(arrow => {
            const arrowAnimation = gsap.fromTo(arrow.position,{
                x: 0,
                z: 0
            },{
                x: arrow.position.x,
                z: arrow.position.z,
                duration: GSAPAnimationMixer.duration,
                ease: GSAPAnimationMixer.ease,
                onStart: () => {
                    arrow.visible = true;
                },
                onComplete: () => {
                    const arrowText = arrow.children.find(text => text.name === arrow.name + `_${currentLang}`);
                    if (!arrowText)
                        return;

                    gsap.fromTo(arrowText.material,{
                        opacity: 0
                    },{
                        opacity: 1,
                        duration: GSAPAnimationMixer.duration * 0.5,
                        ease: GSAPAnimationMixer.ease,
                        onStart: () => {
                            arrowText.material.transparent = true;
                            arrowText.visible = true;
                            CylinderBase.rotateTexts();
                        },
                        onComplete: () => {
                            arrowText.material.transparent = false;
                        }
                    })
                }
            });

            const arrowOpacity = gsap.fromTo(arrow.material,{
                opacity: 0
            },{
                opacity: 1,
                onStart: () => arrow.material.transparent = true,
                onComplete: () => arrow.material.transparent = false,
                duration: GSAPAnimationMixer.duration * 2,
                ease: GSAPAnimationMixer.ease,
            });

            // gridMixer.addAnimation(arrowOpacity);
            gridMixer.addAnimation(arrowAnimation);
        });

        // GSAPAnimationMixer.play('cylinderGrid');
        needsPlay && gridMixer.play();
    }
    
    static updateRotation() {
        CylinderBase.#rotation.y = config.threejs.compass.correction + Math.PI / 2;
        CylinderBase.#group.rotation.setFromVector3(CylinderBase.#rotation);
    }

    static changeLanguage() {
        if (!CylinderBase.#active || !CylinderBase.#ready) return;

        const currentLang = getCurrentLang();
        const newRotationTexts = [];

        CylinderBase.#arrows.forEach(arrow => {
            const visibleText = arrow.children.find(text => text.visible);
            const targetText = arrow.children.find(text => text.name === arrow.name + `_${currentLang}`);

            newRotationTexts.push(targetText);

            if (visibleText && targetText) {
                visibleText.visible = false;
                targetText.visible = true;
            }

        });

        CylinderBase.#rotateToCamera = newRotationTexts;
        CylinderBase.rotateTexts();
    }

    static rotateTexts() {
        if (!CylinderBase.#active || !CylinderBase.#ready) return;

        switch (settingsData.activeViewMode) {
            case ViewMode.Cylinder:
                CylinderBase.#rotateToCamera.forEach(text => {
                    text.rotation.set(
                        Math.PI / 2,
                        rotateToCamera(text.userData.cameraAngle - CylinderBase.#rotation.y + Math.PI),
                        0);
                });

                break;
            case ViewMode.FloorPlan:
                CylinderBase.#rotateToCamera.forEach(text => {
                    text.rotation.set(
                        0,
                        0,
                        rotateToCamera(text.userData.cameraAngle - CylinderBase.#rotation.y + Math.PI)
                    );
                });

                break;

        }
    }

}
